import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { api } from '~/lib/api';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';

type AdminOrgBannerProps = {
  orgName: string;
};

export function AdminOrgBanner({ orgName }: AdminOrgBannerProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const leaveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.admin.organizations.switch.$delete();
      if (!res.ok) throw new Error('Failed to leave organization');
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQueryOptions.queryKey });
      await queryClient.invalidateQueries({ queryKey: departmentsQueryOptions.queryKey });
      void navigate({ to: '/admin/organizations' });
    }
  });

  return (
    <div className="flex shrink-0 items-center justify-between bg-amber-400 px-4 py-2 text-sm font-medium text-amber-950">
      <span>{t('admin.viewingOrg.banner', { name: orgName })}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-auto px-2 py-0.5 text-amber-950 hover:bg-amber-500 hover:text-amber-950"
        disabled={leaveMutation.isPending}
        onClick={() => {
          leaveMutation.mutate();
        }}
      >
        {t('admin.viewingOrg.leave')}
      </Button>
    </div>
  );
}
